import { api, ApiError } from './api';
import { getLocalDb } from './local-db';

export type Session = Awaited<ReturnType<typeof api.session>>;
const IDENTITY_KEY = 'nest-offline-identity';
const SESSION_KEY = 'offlineSession';

export function forgetOfflineSession() {
  localStorage.removeItem(IDENTITY_KEY);
}

async function cachedSession(): Promise<Session | null> {
  const identity = localStorage.getItem(IDENTITY_KEY);
  if (!identity) return null;
  const db = getLocalDb(identity);
  const session = (await db.meta.get(SESSION_KEY))?.value as Session | undefined;
  if (!session || session.user?.id !== identity || !Array.isArray(session.households)) return null;
  // Only advertise households whose local data is actually available.
  const households = await db.households.bulkGet(session.households.map(household => household.id));
  return { ...session, households: session.households.filter((_, index) => households[index]) };
}

export async function loadSession(signal?: AbortSignal): Promise<{ session: Session; offline: boolean }> {
  signal?.throwIfAborted();
  if (!navigator.onLine) {
    const session = await cachedSession();
    if (session) { signal?.throwIfAborted(); return { session, offline: true }; }
  }
  try {
    const session = await api.session(signal);
    signal?.throwIfAborted();
    const db = getLocalDb(session.user.id);
    await db.meta.put({ key: SESSION_KEY, value: session });
    // Store only a locator here; no password, access token, or refresh token.
    localStorage.setItem(IDENTITY_KEY, session.user.id);
    return { session, offline: false };
  } catch (error) {
    signal?.throwIfAborted();
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      forgetOfflineSession();
      throw error;
    }
    const transient = error instanceof TypeError || (error instanceof ApiError && error.status >= 500) || (error instanceof DOMException && error.name === 'TimeoutError');
    if (transient) {
      const session = await cachedSession();
      if (session) { signal?.throwIfAborted(); return { session, offline: true }; }
    }
    throw error;
  }
}
